import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

/* Pre-computed CSS filters that shift the dark-gray SVG fills to match the card accent color.
   Each filter: brightness(0) makes the icon black, then the remaining functions shift to the target hue. */
const COLOR_FILTERS = {
  '#1E3A5F': 'brightness(0) saturate(100%) invert(18%) sepia(30%) saturate(1600%) hue-rotate(181deg) brightness(92%) contrast(90%)',
  '#7D5A00': 'brightness(0) saturate(100%) invert(30%) sepia(90%) saturate(2500%) hue-rotate(14deg) brightness(95%) contrast(103%)',
  '#2E7D32': 'brightness(0) saturate(100%) invert(35%) sepia(60%) saturate(700%) hue-rotate(85deg) brightness(88%) contrast(92%)',
  '#C62828': 'brightness(0) saturate(100%) invert(14%) sepia(90%) saturate(4500%) hue-rotate(350deg) brightness(85%) contrast(108%)',
};

function getFilter(hex) {
  return COLOR_FILTERS[hex] || '';
}

export default function MetricCard({ icon, label, value, subtitle, benchmark, color = '#1E3A5F', bgcolor = '#F7F9FC', dominant = false }) {
  const iconFilter = getFilter(color);

  return (
    <Card
      elevation={dominant ? 3 : 1}
      sx={{
        bgcolor,
        borderTop: `${dominant ? 4 : 3}px solid ${color}`,
        height: '100%',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
        ...(dominant && {
          boxShadow: `0 0 0 1px ${color}20, 0 4px 12px ${color}15`,
        }),
      }}
    >
      <CardContent sx={{ textAlign: 'center', py: { xs: dominant ? 1.75 : 1.25, md: dominant ? 2.5 : 2 }, px: { xs: 1.25, md: 1.75 } }}>
        {icon && (
          <Box
            sx={{
              color,
              mb: 0.5,
              fontSize: { xs: dominant ? 28 : 24, md: dominant ? 36 : 32 },
              '& img': {
                width: { xs: dominant ? 40 : 36, md: dominant ? 48 : 44 },
                height: { xs: dominant ? 40 : 36, md: dominant ? 48 : 44 },
                ...(iconFilter && { filter: iconFilter }),
              },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{
            color,
            mb: 0.5,
            fontFeatureSettings: "'tnum'",
            fontSize: { xs: dominant ? '1.75rem' : '1.5rem', md: dominant ? '2.5rem' : '2.125rem' },
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={dominant ? 600 : 500}
          color="text.primary"
          sx={{ fontSize: { xs: dominant ? '0.8rem' : '0.75rem', md: dominant ? '0.95rem' : '0.875rem' } }}
        >
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: { xs: '0.75rem', md: '0.75rem' } }}>
            {subtitle}
          </Typography>
        )}
        {benchmark && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              color: '#5F6B7A',
              fontStyle: 'italic',
              fontSize: { xs: '0.75rem', md: '0.75rem' },
            }}
          >
            {benchmark}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
